import React, { FC, MouseEvent } from 'react';
import { formatDate, formatPatientName, NO_RESPONSIBLE_USER_PLACEHOLDER } from 'utils/format';
import { TableCell, TableRow, Typography, Box, IconButton } from '@mui/material';
import { Order } from 'models/order';
import RestorationTypes from 'components/orders/shared/restoration-types.component';
import DeliveryDetails from 'components/orders/shared/delivery-details.component';
import OrderItemIconArea from 'components/orders/shared/order-item-icon-area/order-item-icon-area.component';
import OrderStatusChip from 'components/orders/shared/order-status-chip.component';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { OrderItemContext, OrderItemEvent } from 'models/order-item-events';
import { useNavigate } from 'react-router-dom';
import OrderCheckBox from 'components/shared/order-check-box/order-check-box.component';
import { useTranslation } from 'react-i18next';

type DentistOrderItemProps = {
    order: Order;
    onOrderContextMenu: (orderContext: OrderItemContext) => void;
    onOrderItemEvent: (event: OrderItemEvent) => void;
    batchActionSelected?: boolean;
    onHandleCheckedOrders?: (order: Order, checked: boolean, orderIndex: number) => void;
    orderIndex: number;
    isAtLimit?: boolean;
    isChecked?: boolean;
};
const DentistOrderItem: FC<DentistOrderItemProps> = ({
    order,
    onOrderContextMenu,
    onOrderItemEvent,
    batchActionSelected,
    onHandleCheckedOrders,
    orderIndex,
    isAtLimit,
    isChecked,
}: DentistOrderItemProps) => {
    const navigate = useNavigate();
    const { t: translate } = useTranslation(['orders']);

    const navigateToOrderDetails = () => {
        navigate(`/order-details/${order._id}/main-view`);
    };

    const handleContextMenu = (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        onOrderContextMenu({ order: order, mouseX: event.clientX, mouseY: event.clientY });
    };

    return (
        <TableRow
            sx={{ cursor: batchActionSelected ? 'default' : 'pointer' }}
            hover
            onClick={() => {
                if (!batchActionSelected) {
                    navigateToOrderDetails();
                }
            }}
            onContextMenu={handleContextMenu}
        >
            {batchActionSelected ? (
                <TableCell sx={{ padding: 0 }}>
                    <OrderCheckBox
                        order={order}
                        isAtLimit={isAtLimit}
                        checked={isChecked ?? false}
                        onHandleCheckedOrders={
                            onHandleCheckedOrders &&
                            ((checkedOrder, checked) => onHandleCheckedOrders(checkedOrder, checked, orderIndex ?? 0))
                        }
                    />
                </TableCell>
            ) : undefined}
            <TableCell sx={{ maxWidth: '10vw' }}>
                <OrderStatusChip status={order.status} />
            </TableCell>
            <TableCell sx={{ maxWidth: 150 }} className="mouseflow-mask">
                <Box>{formatPatientName(order.patient, translate('orders:NO_PATIENT_REFERENCE'))}</Box>
                {order.patient?.personalIdentityNumber ? (
                    <Typography variant="body2" color="grey.700">
                        {order.patient?.personalIdentityNumber}
                    </Typography>
                ) : order.patient ? (
                    <Typography variant="body2" color="grey.700">
                        {translate('orders:NO_PIN_NUMBER')}
                    </Typography>
                ) : undefined}
            </TableCell>
            <TableCell sx={{ maxWidth: 190 }}>
                <RestorationTypes sampleType={order.deliveryDetails.sampleType} types={order.restorationTypes} />
            </TableCell>
            <TableCell sx={{ maxWidth: 180 }}>{order.dentist?.fullName ?? NO_RESPONSIBLE_USER_PLACEHOLDER}</TableCell>
            <TableCell sx={{ minWidth: 140, maxWidth: 180 }}>{order.supplier.name}</TableCell>
            <TableCell sx={{ minWidth: 160, maxWidth: 175 }}>
                <Box>{formatDate(new Date(order.created.on), 'datetime')}</Box>
                {order.created.by.fullName !== order.dentist?.fullName ? (
                    <Box>
                        <Typography variant="caption">{order.created.by.fullName}</Typography>
                    </Box>
                ) : undefined}
            </TableCell>
            <TableCell sx={{ width: 115 }}>
                <DeliveryDetails details={order.deliveryDetails} />
            </TableCell>
            <TableCell sx={{ maxWidth: '100px', paddingRight: 0.5, paddingLeft: 0.5 }} align="right">
                <OrderItemIconArea order={order} onOrderItemEvent={onOrderItemEvent} />
            </TableCell>
            <TableCell sx={{ maxWidth: '30px', paddingLeft: 0 }}>
                <IconButton size="small" onClick={handleContextMenu}>
                    <MoreVertIcon />
                </IconButton>
            </TableCell>
        </TableRow>
    );
};

export default DentistOrderItem;
